/**
 * Product Sticky Bar
 * Shows when scroll > 500 AND add to cart button is out of viewport
 * Syncs with main product form
 */

class ProductStickyBar extends HTMLElement {
  constructor() {
    super();
    this.isVisible = false;
    this.scrollThreshold = 1200;

    this.checkVisibility = this.checkVisibility.bind(this);
    this.onStickyClick = this.onStickyClick.bind(this);
    this.onOptionsClick = this.onOptionsClick.bind(this);
    this.syncWithForm = this.syncWithForm.bind(this);
  }

  connectedCallback() {
    // Cache elements after DOM is ready
    this.addToCartBtn = document.querySelector("[data-add-to-cart]") || document.querySelector(".main-product-cart-add");
    this.stickyBtn = this.querySelector("[data-sticky-add-to-cart]");
    this.stickyPrice = this.querySelector("[data-sticky-price]");
    this.optionsLink = this.querySelector("[data-scroll-to-form]");

    window.addEventListener("scroll", this.checkVisibility, { passive: true });
    window.addEventListener("resize", this.checkVisibility, { passive: true });

    if (this.stickyBtn) {
      this.stickyBtn.addEventListener("click", this.onStickyClick);
    }

    if (this.optionsLink) {
      this.optionsLink.addEventListener("click", this.onOptionsClick);
    }

    // Listen for variant/price changes from product form
    document.addEventListener("variant:changed", this.syncWithForm);
    document.addEventListener("price:updated", this.syncWithForm);

    // Initial check
    this.checkVisibility();
    this.syncWithForm();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.checkVisibility);
    window.removeEventListener("resize", this.checkVisibility);
    document.removeEventListener("variant:changed", this.syncWithForm);
    document.removeEventListener("price:updated", this.syncWithForm);
  }

  checkVisibility() {
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollPassed = scrollY > this.scrollThreshold;

    // Check if add to cart button is out of viewport
    let buttonOutOfView = scrollPassed;
    if (this.addToCartBtn && this.addToCartBtn.offsetHeight > 0) {
      const rect = this.addToCartBtn.getBoundingClientRect();
      buttonOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;
    }

    const shouldShow = scrollPassed && buttonOutOfView;

    if (shouldShow && !this.isVisible) {
      this.classList.add("is-visible");
      this.isVisible = true;
    } else if (!shouldShow && this.isVisible) {
      this.classList.remove("is-visible");
      this.isVisible = false;
    }
  }

  syncWithForm(e) {
    // If event has detail, use it directly
    if (e && e.detail) {
      const { variantId, tubeVariantId, totalPrice, packagingType } = e.detail;

      if (variantId && this.stickyBtn) {
        this.stickyBtn.dataset.variantId = variantId;
      }

      if (tubeVariantId) {
        this.stickyBtn.dataset.tubeVariantId = tubeVariantId;
      } else {
        delete this.stickyBtn.dataset.tubeVariantId;
      }

      if (totalPrice && this.stickyPrice) {
        this.stickyPrice.textContent = this.formatMoney(totalPrice);
      }

      return;
    }

    // Fallback: sync from DOM
    const mainForm = document.querySelector("main-product-form");
    if (!mainForm) return;

    let variantId = null;
    let price = null;

    // Check tube product buttons
    const activeTubeBtn = mainForm.querySelector(".tube-quantity-btn.active");
    if (activeTubeBtn) {
      variantId = activeTubeBtn.dataset.variantId;
      price = activeTubeBtn.dataset.price;
    }

    // Check vanilla select
    const vanillaSelect = mainForm.querySelector("[data-vanilla-select]");
    if (vanillaSelect && !variantId) {
      const selected = vanillaSelect.options[vanillaSelect.selectedIndex];
      if (selected) {
        variantId = selected.value;
        price = selected.dataset.price;
      }
    }

    // Check coffret hidden input
    const coffretInput = mainForm.querySelector("[data-coffret-variant]");
    if (coffretInput && !variantId) {
      variantId = coffretInput.value;
    }

    // Get price from main add to cart button
    const mainPriceEl = document.querySelector("[data-total-price]");
    if (mainPriceEl) {
      const priceText = mainPriceEl.textContent.trim();
      if (this.stickyPrice) {
        this.stickyPrice.textContent = priceText;
      }
    }

    // Update variant ID
    if (variantId && this.stickyBtn) {
      this.stickyBtn.dataset.variantId = variantId;
    }

    // Check for tube packaging option
    this.checkTubePackaging();
  }

  checkTubePackaging() {
    const mainForm = document.querySelector("main-product-form");
    if (!mainForm) return;

    // Check if tube packaging is selected
    const tubeRadio = mainForm.querySelector('input[name="vanilla_packaging"][value="tube"]:checked');
    if (tubeRadio) {
      // Get selected tube variant
      const tubeInput = mainForm.querySelector("[data-tube-input]:checked");
      if (tubeInput && this.stickyBtn) {
        // Store tube variant for adding
        this.stickyBtn.dataset.tubeVariantId = tubeInput.value;
      }
    } else {
      // Remove tube variant
      if (this.stickyBtn) {
        delete this.stickyBtn.dataset.tubeVariantId;
      }
    }
  }

  async onStickyClick(e) {
    e.preventDefault();

    const variantId = this.stickyBtn.dataset.variantId;
    const tubeVariantId = this.stickyBtn.dataset.tubeVariantId;
    const quantitySelect = document.querySelector("[data-quantity-select]");
    const quantity = quantitySelect ? parseInt(quantitySelect.value, 10) : 1;

    if (!variantId) return;

    // Show loading state
    this.stickyBtn.classList.add("loading");

    try {
      // Add main product
      const items = [{ id: Number(variantId), quantity: quantity }];

      // Add tube if selected
      if (tubeVariantId) {
        items.push({ id: Number(tubeVariantId), quantity: quantity });
      }

      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      });

      // Open side cart
      const sideCart = document.querySelector("side-cart");
      if (sideCart) {
        await sideCart.reloadSidecart();
        sideCart.open();
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      this.stickyBtn.classList.remove("loading");
    }
  }

  onOptionsClick(e) {
    e.preventDefault();

    // Scroll to product form
    const form = document.querySelector("main-product-form");
    if (form) {
      const headerHeight = 80;
      const y = form.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(cents / 100);
  }
}

customElements.define("product-sticky-bar", ProductStickyBar);
